import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Select } from 'ksnm-common-ui/lib/Select';
import { uniqBy } from 'lodash-es';

import { dataControlStore } from '@stores/dataControlStore';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';

import { useGetFilterItems } from './useGetFilterItems';

import styles from './GeneralReportFilter.module.scss';

const filteredItems = (items: { id: string; name: string }[], filters: { [p: string]: string | undefined }) =>
  items.filter((item) =>
    Object.keys(filters).every((f) => {
      if (filters[f]) {
        return item[f] === filters[f];
      }

      return true;
    }),
  );

export const GeneralReportFilter = observer(() => {
  const { organizations } = organizationsStore;
  const { filters, setFilters, addBreadcrumbs, addPage } = dataControlStore;

  const { organization, status, year, reportGroup, period, reportPackage } = filters.generalReport || {};

  const { statusItems, reportGroupItems, yearItems, periodItems, packageItems } = useGetFilterItems(organization);

  const filterReportGroupItems = useMemo(
    () =>
      uniqBy(
        filteredItems(reportGroupItems, { year, period_code: period, report_package_code_cm: reportPackage }),
        'rep_group_code',
      ),
    [period, reportGroupItems, reportPackage, year],
  );

  const filterYearItems = useMemo(
    () =>
      uniqBy(
        filteredItems(yearItems, {
          rep_group_code: reportGroup,
          period_code: period,
          report_package_code_cm: reportPackage,
        }),
        'year',
      ),
    [period, yearItems, reportPackage, reportGroup],
  );

  const filterPeriodItems = useMemo(
    () =>
      uniqBy(
        filteredItems(periodItems, {
          rep_group_code: reportGroup,
          year,
          report_package_code_cm: reportPackage,
        }),
        'period_code',
      ),
    [periodItems, reportGroup, year, reportPackage],
  );

  const filterPackageItems = useMemo(
    () =>
      uniqBy(
        filteredItems(packageItems, {
          rep_group_code: reportGroup,
          year,
          period_code: period,
        }),
        'report_package_code_cm',
      ),
    [packageItems, reportGroup, year, period],
  );

  useEffect(() => {
    if (!organization) {
      const headOrganization = organizations.find((org) => org.head);

      if (headOrganization && headOrganization.code) {
        setFilters({ organization: headOrganization.code }, 'generalReport');

        if (headOrganization.name) addBreadcrumbs([headOrganization.name], true);

        addPage([{ controlType: 'generalReport' }], true);
      }
    }
  }, [addBreadcrumbs, organization, organizations, setFilters, addPage]);

  const onChangeOrganization = useCallback(
    (value) => {
      setFilters({ organization: value }, 'generalReport');

      const name = organizations.find((org) => org.code === value)?.name;
      if (name) addBreadcrumbs([name], true);

      addPage([{ controlType: 'generalReport' }], true);
    },
    [addBreadcrumbs, organizations, setFilters, addPage],
  );

  const onChange = useCallback(
    (value, name) => {
      setFilters({ ...filters.generalReport, [name]: value }, 'generalReport');
    },
    [filters.generalReport, setFilters],
  );

  const handleResetFilters = useCallback(() => {
    setFilters({}, 'generalReport');
  }, [setFilters]);

  const disabled = useMemo(() => !Object.keys(filters.generalReport ?? {}).length, [filters.generalReport]);

  return (
    <div className={styles.designFilters}>
      <div className={styles.filterItems}>
        <Select
          items={organizations}
          placeholder="Выберите организацию"
          value={organization}
          size="small"
          onChange={onChangeOrganization}
          allowClear={false}
          showSearch
          className={styles.organization}
        />

        <Select
          items={statusItems}
          placeholder="Статус"
          value={status}
          size="small"
          onChange={(value) => onChange(value, 'status')}
          showSearch
          className={styles.selectStatus}
        />

        <Select
          items={filterYearItems}
          placeholder="Год"
          value={year}
          size="small"
          onChange={(value) => onChange(value, 'year')}
          showSearch
          className={styles.select}
        />

        <Select
          items={filterReportGroupItems}
          placeholder="Группа отчетности"
          value={reportGroup}
          size="small"
          onChange={(value) => onChange(value, 'reportGroup')}
          showSearch
          className={styles.select}
        />

        <Select
          items={filterPeriodItems}
          placeholder="Период"
          value={period}
          size="small"
          onChange={(value) => onChange(value, 'period')}
          showSearch
          className={styles.select}
        />

        <Select
          items={filterPackageItems}
          placeholder="Код пакета"
          value={reportPackage}
          size="small"
          onChange={(value) => onChange(value, 'reportPackage')}
          showSearch
          className={styles.select}
        />
      </div>

      <Button onClick={handleResetFilters} disabled={disabled} icon={<Icon name="stop" />}>
        Сбросить фильтры
      </Button>
    </div>
  );
});
